// Content + options for the tour operator "register interest" page
// (/register-interest — the QR shown at the end of event pitches).
// Niche IDs are stored in Supabase (tour_operator_interests.niches) — keep them
// in sync with the check constraint in the migration when adding options.

import { liveDistrictsLabel, openingNextDistricts } from '@/lib/live-districts'
import type { FaqItem } from '@/lib/seo'

export const nicheOptions = [
  { value: 'inbound', label: 'Inbound tours' },
  { value: 'domestic', label: 'Domestic tours' },
  { value: 'heritage_culture', label: 'Heritage & culture' },
  { value: 'pilgrimage', label: 'Pilgrimage' },
  { value: 'educational', label: 'Student groups' },
  { value: 'corporate_mice', label: 'Corporate & MICE' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'other', label: 'Other' },
] as const

export const nicheIds = nicheOptions.map((option) => option.value) as [
  (typeof nicheOptions)[number]['value'],
  ...(typeof nicheOptions)[number]['value'][],
]

export type Niche = (typeof nicheOptions)[number]['value']

export interface PartnerProduct {
  title: string
  tagline: string
  image: string
  imageAlt: string
  href: string
}

// Shown below the form so operators can see what they would be selling.
export const partnerProducts: PartnerProduct[] = [
  {
    title: 'Hosted experiences',
    tagline: '1–1.5 hour craft, farm and food sessions with local hosts.',
    image: '/weekend-trips/silk-weaving-loom.webp',
    imageAlt: 'Silk saree taking shape on a Thirubhuvanam handloom',
    href: '/experiences',
  },
  {
    title: 'A Day in Thanjavur',
    tagline: 'A ready-made hosted day on fixed weekend dates.',
    image: '/weekend-trips/betel-farm-corridor.webp',
    imageAlt: 'Betel vine corridor on the Thanjavur weekend farm walk',
    href: '/weekend-trips',
  },
  {
    title: 'Custom trails',
    tagline: 'Multi-day routes shaped around your groups.',
    image: '/experiences/paddy-harvesting-in-wet-fields.jpg',
    imageAlt: 'Paddy harvest in wet fields in Tamil Nadu',
    href: '/collaborations',
  },
  {
    title: 'Student journeys',
    tagline: 'Field programmes for schools and colleges.',
    image: '/riip-image.jpeg',
    imageAlt: 'Students documenting rural stories on an Oorva programme',
    href: '/riip',
  },
]

/** Shown on the success screen — the promise the partnerships team keeps. */
export const PARTNER_RESPONSE_TIME = 'within 2 working days'

// Partner catalogue: downloadable from the success screen and attached to the
// thank-you email (/api/partner-catalogue). The file lives in /public.
export const PARTNER_CATALOGUE_PATH = '/oorva-catalogue-2026.pdf'
export const PARTNER_CATALOGUE_FILENAME = 'Oorva Catalogue 2026.pdf'

// Rendered on the page and as FAQPage structured data — keep the two identical.
export const partnerFaqs: FaqItem[] = [
  {
    q: 'Who can partner with Oorva?',
    a: 'Inbound and domestic tour operators, DMCs, travel agencies, student and educational tour organisers, and corporate or MICE planners who bring guests to Tamil Nadu.',
  },
  {
    q: 'What experiences can I add to my itineraries?',
    a: 'Hosted 1–1.5 hour sessions with local artisans, farmers and cooks, such as Thanjavur doll making, Thirubhuvanam silk weaving and a Kumbakonam betel farm walk. We also run A Day in Thanjavur on weekends, custom multi-day trails and field programmes for students.',
  },
  {
    q: 'Where does Oorva operate?',
    a: `Oorva is live in ${liveDistrictsLabel}, Tamil Nadu, with ${openingNextDistricts.join(', ')} opening next. We work with the Department of Tourism, Tamil Nadu and Guidance Tamil Nadu.`,
  },
  {
    q: 'How do bookings work for tour operators?',
    a: 'You share your dates and group size, and the Oorva team confirms every booking with our hosts on WhatsApp. One Oorva contact coordinates your guests on the ground.',
  },
  {
    q: 'What happens after I register interest?',
    a: `You can download our 2026 partner catalogue straight away, and we email you a copy if you share your email. Our partnerships team then messages you on WhatsApp ${PARTNER_RESPONSE_TIME} with partner details for your niche. Registering is free and there is no commitment.`,
  },
]

export function nicheLabels(niches: readonly string[], other?: string) {
  return niches.map((niche) => {
    if (niche === 'other' && other?.trim()) return `Other: ${other.trim()}`
    return nicheOptions.find((option) => option.value === niche)?.label ?? niche
  })
}
